import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Clock, ChevronDown, Loader2 } from 'lucide-react'
import { api, formatDateTime } from '@/lib/api'

// Linha "último X cadastrado" + lista dos N mais recentes POR DATA.
// Pedido do usuário (26/06): mostrar na tela de cadastro de cliente o último
// cliente cadastrado, com opção de ver a lista dos últimos por data — e o mesmo
// pra produtos. Reusa o endpoint enxuto /cadastros/{recurso}/recentes
// (criado_em DESC, respeita tenant). Datas via formatDateTime (UTC→Brasília).
interface Recente {
  id: number
  codigo?: string | null
  nome: string
  criado_em?: string | null
}

export default function UltimosCadastros({
  endpoint,
  params,
  queryKey,
  rotulo,
  onSelecionar,
}: {
  endpoint: string                       // ex: '/cadastros/pessoas/recentes'
  params?: Record<string, string>        // ex: { tipo: 'cliente' }
  queryKey: unknown[]                    // chave do react-query (inclui o tipo)
  rotulo: string                         // ex: 'cliente' | 'produto'
  onSelecionar?: (id: number) => void    // clique num item da lista (opcional)
}) {
  const [aberto, setAberto] = useState(false)
  const { data = [], isLoading, isFetching } = useQuery<Recente[]>({
    queryKey,
    queryFn: async () => (await api.get(endpoint, { params: { ...params, limit: 10 } })).data,
    staleTime: 30_000,
  })

  const ultimo = data[0]

  return (
    <div className="rounded-xl border border-ink-200 bg-white">
      <button
        type="button"
        onClick={() => setAberto(a => !a)}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left">
        <Clock className="w-4 h-4 text-brand-600 shrink-0" />
        <span className="text-ink-500">Último {rotulo} cadastrado:</span>
        {isLoading ? (
          <span className="text-ink-400 flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /> carregando…</span>
        ) : ultimo ? (
          <span className="font-medium text-ink-900 truncate">
            {ultimo.codigo ? `${ultimo.codigo} · ` : ''}{ultimo.nome}
            <span className="text-ink-400 font-normal"> — {formatDateTime(ultimo.criado_em)}</span>
          </span>
        ) : (
          <span className="text-ink-400">nenhum ainda</span>
        )}
        <span className="ml-auto flex items-center gap-1 text-brand-600 shrink-0">
          {isFetching && !isLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          ver últimos
          <ChevronDown className={`w-4 h-4 transition-transform ${aberto ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {aberto && (
        <div className="border-t border-ink-200 max-h-72 overflow-auto">
          {data.length === 0 && !isLoading && (
            <div className="px-3 py-3 text-sm text-ink-400">Nenhum {rotulo} cadastrado ainda.</div>
          )}
          {data.map((r, i) => (
            <div
              key={r.id}
              onClick={() => onSelecionar?.(r.id)}
              className={`flex items-center gap-3 px-3 py-2 text-sm ${onSelecionar ? 'cursor-pointer hover:bg-ink-50' : ''} ${i > 0 ? 'border-t border-ink-100' : ''}`}>
              {r.codigo && <span className="text-xs font-mono text-ink-400 shrink-0">{r.codigo}</span>}
              <span className="font-medium text-ink-800 truncate flex-1">{r.nome}</span>
              <span className="text-xs text-ink-400 shrink-0">{formatDateTime(r.criado_em)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
